// ─── 📄 Il foglio che sale da sotto ──────────────────────────────────────────
//
// 🗣️ 19/09/2026, guardando il prototipo: *«se qua mi dai la possibilità di
// modificare, mi deve aprire una schermata più semplice di quella attuale»*.
//
// ⚖️⭐⭐ **PERCHÉ UN FOGLIO E NON UN'ALTRA SCHERMATA.** Cambiare la durata di
// una lezione, o il campo, è un gesto che **finisce dov'è cominciato**: si
// guarda la scheda, si tocca una riga, si sceglie, e si torna a guardare la
// stessa scheda. Mandare a un altro indirizzo vorrebbe dire uscire e rientrare
// — e al ritorno lo scorrimento riparte da capo, cioè si perde il punto in cui
// si stava guardando.
// 📌 *Un gesto che non cambia argomento non deve cambiare schermata.*
//
// ⛔ **E NON È UN DIALOGO IN MEZZO ALLO SCHERMO**: su un telefono un dialogo
//    centrato mette i bottoni dove il pollice non arriva, e copre proprio la
//    cosa che si sta decidendo. Questo sale dal basso, copre **la metà bassa**,
//    e lascia in vista la riga da cui si è partiti.
//
// ⭐ La maniglia sta **dentro** la testa e non sopra: lì resta ferma con lei
//   mentre il contenuto scorre.

import { useEffect, useRef } from 'react';

export function FoglioBasso({
  titolo,
  chiudi,
  accanto,
  children,
}: {
  titolo: string;
  chiudi: () => void;
  /** Una cosa da scrivere accanto al titolo: «2 di 6», e niente di più lungo. */
  accanto?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  const foglio = useRef<HTMLDivElement | null>(null);

  /**
   * ⌨️ **Esc chiude**, e il fuoco entra dentro.
   *
   * ⚖️ Non è cortesia per la tastiera: il fuoco che resta **sotto** il velo è
   * il modo in cui un foglio aperto si può usare senza vederlo — si tocca un
   * bottone della scheda coperta credendo di toccare quello del foglio.
   * ⚠️ E il corpo si ferma finché il foglio è su: se no scorre la pagina
   *    dietro, e riaprendo gli occhi si è in un altro punto della scheda.
   */
  /* 🚨⭐ **`chiudi` sta in un riferimento, e l'effetto gira UNA volta sola.**
     Misurato il 23/09 dopo una sua segnalazione (*«la nota non funziona poterla
     scrivere dentro»*): l'effetto dipendeva da `chiudi`, e chi apre il foglio
     spesso lo passa scritto al volo, cioè nuovo a ogni disegno. Scrivendo nella
     nota ogni lettera ridisegna la scheda ⇒ l'effetto ripartiva ⇒ il fuoco
     tornava sul foglio. Risultato: entrava la prima lettera, e le altre no.
     📌 *Un effetto che deve succedere all'apertura non può dipendere da una
        cosa che cambia a ogni battuta.* */
  const chiudiOra = useRef(chiudi);
  chiudiOra.current = chiudi;

  useEffect(() => {
    const fermo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    foglio.current?.focus();
    const tasto = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') chiudiOra.current();
    };
    document.addEventListener('keydown', tasto);
    return () => {
      document.removeEventListener('keydown', tasto);
      document.body.style.overflow = fermo;
    };
  }, []);

  return (
    /* ⚠️ Il velo chiude **solo se si tocca lui**: senza il confronto, un tocco
       partito da dentro il foglio e finito sul velo — cioè un dito che scorre
       un elenco — lo richiuderebbe in faccia a chi sta scegliendo. */
    <div className="velo-maestro" onMouseDown={(e) => e.target === e.currentTarget && chiudi()}>
      <div
        className="foglio-maestro"
        role="dialog"
        aria-modal="true"
        aria-label={titolo}
        tabIndex={-1}
        ref={foglio}
      >
        <div className="testa-foglio">
          <div className="maniglia-maestro" aria-hidden="true" />
          <div className="capo-foglio">
            <h2>{titolo}</h2>
            {accanto}
            <button type="button" className="piatto" onClick={chiudi}>
              Chiudi
            </button>
          </div>
        </div>
        <div className="dentro-foglio">{children}</div>
      </div>
    </div>
  );
}
